import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiSend, FiRefreshCw } from 'react-icons/fi';
import { ArtifactoryMarkdown } from './ArtifactoryMarkdown';

interface Message {
  id: string;
  role: 'user' | 'system' | 'assistant';
  content: string;
  timestamp: Date;
  isIntent?: boolean;
}

interface ArtifactoryChatProps {
  sessionId: string;
  manifestId: string;
  initialPrompt: string;
  onUpdateIntent?: (newIntent: string) => Promise<void>;
}

export const ArtifactoryChat: React.FC<ArtifactoryChatProps> = ({ 
  sessionId, 
  manifestId,
  initialPrompt,
  onUpdateIntent 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { darkMode } = useTheme();

  // Initialize with the initial prompt
  useEffect(() => {
    // Only set messages if they haven't been set already
    if (messages.length === 0) {
      const promptToUse = initialPrompt?.trim() ? initialPrompt : "Initial project structure";
      
      setMessages([
        {
          id: '1',
          role: 'user',
          content: promptToUse,
          timestamp: new Date(),
          isIntent: true
        },
        {
          id: '2',
          role: 'system',
          content: 'Project structure has been scaffolded based on your requirements. You can ask questions or request changes to the structure.',
          timestamp: new Date()
        }
      ]);
    }
  }, [initialPrompt, messages.length]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim() || isProcessing) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);

    try {
      // Add a processing message
      const processingMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Processing your request to update the project structure...',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, processingMessage]);
      
      // Call the actual API to update the project
      if (onUpdateIntent) {
        await onUpdateIntent(input);
        
        // Add a success message
        const successMessage: Message = {
          id: (Date.now() + 2).toString(),
          role: 'system',
          content: 'Project structure has been updated successfully.',
          timestamp: new Date()
        };
        
        setMessages(prev => {
          // Replace the processing message with the success message
          const newMessages = [...prev];
          const processingIndex = newMessages.findIndex(m => m.id === processingMessage.id);
          if (processingIndex !== -1) {
            newMessages[processingIndex] = successMessage;
          } else {
            newMessages.push(successMessage);
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error('Error processing message:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
        role: 'system',
        content: `An error occurred: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date()
      };
      
      setMessages(prev => {
        // Replace the processing message with the error message
        const newMessages = [...prev];
        const processingIndex = newMessages.findIndex(m => m.id === (Date.now() + 1).toString());
        if (processingIndex !== -1) {
          newMessages[processingIndex] = errorMessage;
        } else {
          newMessages.push(errorMessage);
        }
        return newMessages;
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden min-h-0 h-full">
      {/* Chat Header */}
      <div className={`py-1 px-2 border-b flex-shrink-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
        <h2 className="font-semibold text-sm">Project Intent</h2>
      </div>

      {/* Messages Area — scrolls internally, never pushes input off screen */}
      <div className="overflow-y-auto p-4 space-y-4 flex-1 min-h-0">
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-3/4 rounded-lg p-3 ${
                message.role === 'user' 
                  ? message.isIntent
                    ? darkMode 
                      ? 'bg-purple-800 text-white border border-purple-700' 
                      : 'bg-purple-100 text-purple-900 border border-purple-200'
                    : darkMode 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-500 text-white'
                  : message.role === 'system'
                    ? darkMode
                      ? 'bg-gray-700 text-gray-200'
                      : 'bg-gray-200 text-gray-800'
                    : darkMode
                      ? 'bg-gray-800 text-gray-200'
                      : 'bg-white text-gray-800 border border-gray-200'
              }`}
            >
              {message.isIntent && (
                <div className={`text-xs mb-1 font-semibold ${
                  darkMode ? 'text-purple-300' : 'text-purple-700'
                }`}>
                  INITIAL PROJECT INTENT
                </div>
              )}
              {message.role === 'assistant' || message.isIntent ? (
                <ArtifactoryMarkdown content={message.content} size="small" />
              ) : (
                <p className="whitespace-pre-wrap">{message.content}</p>
              )}
              <div className={`text-xs mt-1 ${
                message.role === 'user' 
                  ? message.isIntent
                    ? darkMode ? 'text-purple-300' : 'text-purple-700'
                    : darkMode ? 'text-blue-200' : 'text-blue-100'
                  : darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input Area — pinned to bottom, never inside scroll container */}
      <div className={`py-1 px-2 border-t flex-shrink-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your request..."
            className={`flex-1 p-2 rounded-l-md ${
              darkMode 
                ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
            } border focus:outline-none`}
            disabled={isProcessing}
          />
          <button
            onClick={handleSendMessage}
            disabled={isProcessing}
            className={`p-2 rounded-r-md ${
              isProcessing
                ? darkMode 
                  ? 'bg-gray-700 text-gray-500' 
                  : 'bg-gray-200 text-gray-500'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isProcessing ? <FiRefreshCw className="animate-spin" /> : <FiSend />}
          </button>
        </div>
      </div>
    </div>
  );
};
